if (!customElements.get('product-highlights')) {
  class ProductHighlights extends HTMLElement {
    constructor() {
      super();
      this.scroller = this.querySelector('.highlights__scroller');
      this.cards = Array.from(this.querySelectorAll('.highlights__card'));
      this.dots = Array.from(this.querySelectorAll('.highlights__dot'));
      this.fills = this.dots.map((dot) => dot.querySelector('.highlights__dot-fill'));
      this.playPauseButton = this.querySelector('.highlights__playpause');
      this.prevPaddle = this.querySelector('.highlights__btn--prev');
      this.nextPaddle = this.querySelector('.highlights__btn--next');

      this.autoplay = this.dataset.autoplay !== 'false';
      this.speed = parseFloat(this.dataset.speed) || 4;
      this.center = this.dataset.center === 'true';
      this.parallax = this.dataset.parallax !== 'false';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.isPlaying = false;
      this.current = 0;
      this.timer = null;
      this.inView = true;

      this.onScroll = this.onScroll.bind(this);
      this.onCardPointerDown = this.onCardPointerDown.bind(this);
      this.onCardPointerMove = this.onCardPointerMove.bind(this);
      this.onCardPointerUp = this.onCardPointerUp.bind(this);
    }

    connectedCallback() {
      if (!this.cards.length || !this.scroller) return;

      this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
      this.scroller.addEventListener('pointerdown', this.onCardPointerDown);

      if (this.playPauseButton) this.playPauseButton.addEventListener('click', () => this.toggle());
      if (this.prevPaddle) this.prevPaddle.addEventListener('click', () => this.goTo(this.current - 1));
      if (this.nextPaddle) this.nextPaddle.addEventListener('click', () => this.goTo(this.current + 1));

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting;
              if (entry.isIntersecting) {
                if (this.autoplay) this.play();
                this.syncVideos();
              } else {
                this.pause();
              }
            });
          },
          { threshold: 0.25 }
        );
        this.observer.observe(this);
      } else if (this.autoplay) {
        this.play();
      }

      if (!this.reducedMotion) this.classList.add('is-reveal-ready');
      this.setupReveal();

      this.updateActive(0);
      this.syncVideos();
      this.updateParallax();
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.observer) this.observer.disconnect();
      if (this.revealObserver) this.revealObserver.disconnect();
    }

    /* ---- Caption reveal (light fade-in when a card enters the viewport) ---- */
    setupReveal() {
      if (this.reducedMotion) return;
      if (!('IntersectionObserver' in window)) {
        this.cards.forEach((c) => c.classList.add('is-revealed'));
        return;
      }
      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio >= 0.5) {
              entry.target.classList.add('is-revealed');
            } else if (entry.intersectionRatio < 0.1) {
              entry.target.classList.remove('is-revealed');
            }
          });
        },
        { root: this.scroller, threshold: [0, 0.1, 0.5, 1] }
      );
      this.cards.forEach((c) => this.revealObserver.observe(c));
    }

    /* ---- Horizontal parallax for the caption text ---- */
    updateParallax() {
      if (!this.parallax || this.reducedMotion) return;
      const rect = this.scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      this.cards.forEach((card) => {
        const text = card.querySelector('.highlights__card-text');
        if (!text) return;
        const cr = card.getBoundingClientRect();
        const delta = cr.left + cr.width / 2 - center;
        const shift = Math.max(-60, Math.min(60, delta * 0.1));
        text.style.transform = `translate3d(${shift.toFixed(1)}px, 0, 0)`;
      });
    }

    /* ---- Geometry ---- */
    get maxScroll() {
      return Math.max(0, this.scroller.scrollWidth - this.scroller.clientWidth);
    }

    getEdgePadding() {
      return parseFloat(getComputedStyle(this.scroller).paddingLeft) || 0;
    }

    getCurrentIndex() {
      const base = this.scroller.getBoundingClientRect().left;
      const ref = this.center
        ? this.scroller.scrollLeft + this.scroller.clientWidth / 2
        : this.scroller.scrollLeft + this.getEdgePadding();
      let closest = 0;
      let min = Infinity;
      this.cards.forEach((card, i) => {
        const cr = card.getBoundingClientRect();
        const left = this.center
          ? this.scroller.scrollLeft + (cr.left - base) + cr.width / 2
          : this.scroller.scrollLeft + (cr.left - base);
        const dist = Math.abs(left - ref);
        if (dist < min) {
          min = dist;
          closest = i;
        }
      });
      return closest;
    }

    getDuration(index) {
      const video = this.cards[index]?.querySelector('video');
      if (video && !Number.isNaN(video.duration) && video.duration > 0) {
        return video.duration;
      }
      return this.speed;
    }

    /* ---- Active dot state ---- */
    updateActive(index) {
      this.current = index;
      this.dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        const fill = this.fills[i];
        if (!fill) return;
        fill.style.transition = 'none';
        // Inactive dots: empty. Active dot: full by default (animated when playing).
        fill.style.transform = active ? 'scaleX(1)' : 'scaleX(0)';
      });
      if (this.prevPaddle) this.prevPaddle.disabled = index <= 0;
      if (this.nextPaddle) this.nextPaddle.disabled = index >= this.cards.length - 1;
    }

    /* ---- Progress fill of the active dot ---- */
    startFill(index) {
      const fill = this.fills[index];
      if (!fill) return;
      const duration = this.getDuration(index);
      fill.style.transition = 'none';
      fill.style.transform = 'scaleX(0)';
      // Force reflow so the transition restarts reliably
      void fill.offsetWidth;
      fill.style.transition = `transform ${duration}s linear`;
      fill.style.transform = 'scaleX(1)';
    }

    freezeFill() {
      const fill = this.fills[this.current];
      if (!fill) return;
      const matrix = getComputedStyle(fill).transform;
      let scaleX = 1;
      if (matrix && matrix !== 'none') {
        const values = matrix.match(/matrix\(([^)]+)\)/);
        if (values) scaleX = parseFloat(values[1].split(',')[0]) || 0;
      }
      fill.style.transition = 'none';
      fill.style.transform = `scaleX(${scaleX})`;
    }

    /* ---- Navigation ---- */
    goTo(index) {
      const i = Math.max(0, Math.min(index, this.cards.length - 1));
      this.updateActive(i);
      this.scrollToIndex(i);
      this.syncVideos();
      if (this.isPlaying && this.autoplay && this.inView) {
        this.startSlide(i);
      }
    }

    scrollToIndex(index) {
      const card = this.cards[index];
      if (!card) return;
      const base = this.scroller.getBoundingClientRect().left;
      const cr = card.getBoundingClientRect();
      let target;
      if (this.center) {
        const cardCenter = this.scroller.scrollLeft + (cr.left - base) + cr.width / 2;
        target = cardCenter - this.scroller.clientWidth / 2;
      } else {
        target = this.scroller.scrollLeft + (cr.left - base) - this.getEdgePadding();
      }
      this._programmatic = true;
      clearTimeout(this._progTimer);
      this._progTimer = setTimeout(() => {
        this._programmatic = false;
      }, 700);
      this.scroller.scrollTo({
        left: Math.max(0, Math.min(target, this.maxScroll)),
        behavior: 'smooth',
      });
    }

    /* ---- Autoplay with progress ---- */
    startSlide(index) {
      this.stopTimer();
      this.startFill(index);
      const duration = this.getDuration(index) * 1000;
      this.timer = setTimeout(() => {
        const next = index >= this.cards.length - 1 ? 0 : index + 1;
        this.goTo(next);
      }, duration);
    }

    stopTimer() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
    }

    toggle() {
      if (this.isPlaying) this.pause();
      else this.play();
    }

    play() {
      if (this.isPlaying || this.cards.length < 2) return;
      this.isPlaying = true;
      if (this.playPauseButton) this.playPauseButton.setAttribute('aria-pressed', 'false');
      this.syncVideos();
      if (this.autoplay) this.startSlide(this.current);
    }

    pause() {
      this.isPlaying = false;
      if (this.playPauseButton) this.playPauseButton.setAttribute('aria-pressed', 'true');
      this.stopTimer();
      this.freezeFill();
      this.pauseAllVideos();
    }

    /* ---- Videos: play only those visible in the viewport ---- */
    syncVideos() {
      const viewRect = this.scroller.getBoundingClientRect();
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;
        const r = card.getBoundingClientRect();
        const visible = r.right > viewRect.left + 20 && r.left < viewRect.right - 20;
        if (visible && (this.isPlaying || !this.autoplay) && this.inView) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    pauseAllVideos() {
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (video) video.pause();
      });
    }

    /* ---- Scroll sync ---- */
    onScroll() {
      if (this._videoRaf) cancelAnimationFrame(this._videoRaf);
      this._videoRaf = requestAnimationFrame(() => {
        this.syncVideos();
        this.updateParallax();
      });

      if (this._programmatic) return;

      const idx = this.getCurrentIndex();
      if (idx !== this.current) this.updateActive(idx);

      if (this.cardDown) return;
      clearTimeout(this._settle);
      this._settle = setTimeout(() => {
        if (this.isPlaying && this.autoplay && this.inView) this.startSlide(this.current);
      }, 160);
    }

    /* ---- Drag the cards ---- */
    onCardPointerDown(event) {
      if (event.button !== 0) return;
      // On desktop, disable mouse drag (the card stays in place / returns back).
      if (event.pointerType === 'mouse' && window.matchMedia('(min-width: 990px)').matches) return;
      this.cardDown = true;
      this.cardMoved = false;
      this.startX = event.pageX;
      this.startScroll = this.scroller.scrollLeft;
      this.stopTimer();
      this.freezeFill();
      this.scroller.setPointerCapture?.(event.pointerId);
      this.scroller.addEventListener('pointermove', this.onCardPointerMove);
      this.scroller.addEventListener('pointerup', this.onCardPointerUp);
      this.scroller.addEventListener('pointercancel', this.onCardPointerUp);
    }

    onCardPointerMove(event) {
      if (!this.cardDown) return;
      const walk = event.pageX - this.startX;
      if (Math.abs(walk) > 4) {
        this.cardMoved = true;
        this.scroller.classList.add('is-dragging');
      }
      this.scroller.scrollLeft = this.startScroll - walk;
    }

    onCardPointerUp() {
      this.cardDown = false;
      this.scroller.classList.remove('is-dragging');
      this.scroller.removeEventListener('pointermove', this.onCardPointerMove);
      this.scroller.removeEventListener('pointerup', this.onCardPointerUp);
      this.scroller.removeEventListener('pointercancel', this.onCardPointerUp);

      const idx = this.getCurrentIndex();
      this.updateActive(idx);
      if (this.isPlaying && this.autoplay && this.inView) this.startSlide(idx);

      if (this.cardMoved) {
        const prevent = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.scroller.removeEventListener('click', prevent, true);
        };
        this.scroller.addEventListener('click', prevent, true);
      }
    }
  }

  customElements.define('product-highlights', ProductHighlights);
}
